import { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { API_BASE_URL } from '../../constants';
import './EditProjectModal.css';

function EditProjectModal({ project, onClose, onProjectUpdated, onDelete, deleting }) {
  const [name, setName] = useState(project?.name || '');
  const [description, setDescription] = useState(project?.description || '');
  const [price, setPrice] = useState(
    project?.price != null ? Number(project.price).toFixed(2) : ''
  );
  const [imageFiles, setImageFiles] = useState([]);
  const [stepFile, setStepFile] = useState(null);
  const [xmlFile, setXmlFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const existingImageNames = project?.imageFileNames?.length
    ? project.imageFileNames
    : project?.imageFileName
    ? [project.imageFileName]
    : [];
  const existingImageUrls = project?.imageUrls?.length
    ? project.imageUrls
    : project?.imageUrl
    ? [project.imageUrl]
    : [];

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      setImageFiles([]);
      return;
    }

    const oversize = files.find((file) => file.size > 5 * 1024 * 1024);
    if (oversize) {
      setError('Each image must be less than 5MB');
      event.target.value = '';
      return;
    }

    setImageFiles((prev) => [...prev, ...files]);
    setError('');
    event.target.value = '';
  };

  const removeImageAt = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleStepChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setStepFile(null);
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      setError('STEP file must be less than 50MB');
      return;
    }

    setStepFile(file);
    setError('');
  };

  const handleXmlChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      setXmlFile(null);
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('XML file must be less than 10MB');
      return;
    }

    setXmlFile(file);
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    const priceVal = parseFloat(price);
    if (price && priceVal > 0 && priceVal < 0.50) {
      setError('Minimum price is $0.50 (or leave empty/0 for free)');
      return;
    }

    setSaving(true);

    try {
      const formData = new FormData();
      formData.append('docId', project.id);
      formData.append('userId', project.ownerId);
      formData.append('projectId', project.projectId);
      formData.append('projectName', name.trim());
      formData.append('description', description || '');
      formData.append('folderName', project.folderName);
      formData.append('existingImageFileName', project.imageFileName || '');
      formData.append('existingStepFileName', project.stepFileName || '');
      formData.append('existingXmlFileName', project.xmlFileName || '');

      if (imageFiles.length) {
        imageFiles.forEach((file) => formData.append('image', file));
      }

      if (stepFile) {
        formData.append('step', stepFile);
      }

      if (xmlFile) {
        formData.append('xml', xmlFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/update-project`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${await auth.currentUser?.getIdToken()}`,
        },
        body: formData,
      });

      const result = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(result?.error || 'Failed to update project');
      }

      if (!result || !result.success) {
        throw new Error(result.error || 'Failed to update project');
      }

      const projectRef = doc(db, 'marketplace-projects', project.id);

      const updates = {
        name: name.trim(),
        description: description || '',
        price: price ? parseFloat(parseFloat(price).toFixed(2)) : 0,
        currency: 'usd',
        updatedAt: serverTimestamp(),
      };

      const newImageUrls = Array.isArray(result.imageUrls)
        ? result.imageUrls
        : result.imageUrl
        ? [result.imageUrl]
        : [];
      if (newImageUrls.length) {
        const mergedUrls = Array.from(new Set([...(existingImageUrls || []), ...newImageUrls]));
        updates.imageUrls = mergedUrls;
        updates.imageUrl = mergedUrls[0];
      }

      if (result.stepFilePath) {
        updates.stepFilePath = result.stepFilePath;
      }

      if (result.xmlFilePath) {
        updates.xmlFilePath = result.xmlFilePath;
      }

      const newImageFileNames = Array.isArray(result.imageFileNames)
        ? result.imageFileNames
        : Array.isArray(result.fileNames?.image)
        ? result.fileNames.image
        : result.fileNames?.image
        ? [result.fileNames.image]
        : result.imageFileName
        ? [result.imageFileName]
        : [];
      if (newImageFileNames.length) {
        const mergedNames = Array.from(new Set([...(existingImageNames || []), ...newImageFileNames]));
        updates.imageFileNames = mergedNames;
        updates.imageFileName = mergedNames[0];
      }

      if (result.fileNames?.step) {
        updates.stepFileName = result.fileNames.step;
      }

      if (result.fileNames?.xml) {
        updates.xmlFileName = result.fileNames.xml;
      }

      await updateDoc(projectRef, updates);

      const mergedImageUrls = updates.imageUrls || project.imageUrls || existingImageUrls;
      const mergedImageFileNames = updates.imageFileNames || project.imageFileNames || existingImageNames;

      const updatedProject = {
        ...project,
        name: name.trim(),
        description: description || '',
        imageUrl: (updates.imageUrl || project.imageUrl),
        imageFileName: updates.imageFileName || project.imageFileName,
        imageUrls: mergedImageUrls,
        imageFileNames: mergedImageFileNames,
        stepFilePath: result.stepFilePath || project.stepFilePath,
        stepFileName: result.fileNames?.step || project.stepFileName,
        xmlFilePath: result.xmlFilePath || project.xmlFilePath,
        xmlFileName: result.fileNames?.xml || project.xmlFileName,
      };

      onProjectUpdated(updatedProject);
      onClose();
    } catch (err) {
      console.error('Update error:', err);
      setError(err.message || 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setError('');
    try {
      await onDelete();
    } catch (err) {
      console.error('Delete error:', err);
      setError(err.message || 'Failed to delete project');
    }
  };

  const isBusy = saving || deleting;

  return (
    <div className="edit-modal-overlay" onClick={isBusy ? undefined : onClose}>
      <div className="edit-modal" onClick={(event) => event.stopPropagation()}>
        <div className="edit-modal__header">
          <h2>Edit Project</h2>
          <button
            className="edit-modal__close"
            onClick={onClose}
            disabled={isBusy}
          >
            ✕
          </button>
        </div>

        <form className="edit-modal__form" onSubmit={handleSubmit}>
          {error && <div className="edit-modal__error">{error}</div>}

          <div className="edit-modal__field">
            <label>Project Name *</label>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="My Robot Behavior Tree"
              disabled={isBusy}
              required
            />
          </div>

          <div className="edit-modal__field">
            <label>Description</label>
            <textarea
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Describe your project..."
              rows={3}
              disabled={isBusy}
            />
          </div>

          <div className="edit-modal__field">
            <label>Price (USD) — leave empty or 0 for free</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              placeholder="0.00"
              disabled={isBusy}
            />
          </div>

          <div className="edit-modal__divider" />

          <div className="edit-modal__group">
            <div className="edit-modal__field">
              <label>Project Images</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
                disabled={isBusy}
              />
              {existingImageNames.length ? (
                <ul className="edit-modal__file-list">
                  {existingImageNames.map((name, index) => (
                    <li key={`${name}-${index}`} className="edit-modal__file-list-item">
                      <span>Current: {name}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <span className="edit-modal__filename">
                  Current: None uploaded
                </span>
              )}
              {imageFiles.length > 0 && (
                <ul className="edit-modal__file-list edit-modal__file-list--new">
                  {imageFiles.map((file, index) => (
                    <li key={`${file.name}-${index}`} className="edit-modal__file-list-item">
                      <span>New: {file.name}</span>
                      <button
                        type="button"
                        className="edit-modal__file-clear"
                        onClick={() => removeImageAt(index)}
                        disabled={isBusy}
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="edit-modal__field">
              <label>STEP File</label>
              <input
                type="file"
                accept=".step,.stp"
                onChange={handleStepChange}
                disabled={isBusy}
              />
              <span className="edit-modal__filename">
                Current:&nbsp;
                {project.stepFileName || 'None uploaded'}
              </span>
              {stepFile && (
                <span className="edit-modal__filename edit-modal__filename--new">
                  New: {stepFile.name}
                </span>
              )}
            </div>

            <div className="edit-modal__field">
              <label>XML File</label>
              <input
                type="file"
                accept=".xml"
                onChange={handleXmlChange}
                disabled={isBusy}
              />
              <span className="edit-modal__filename">
                Current:&nbsp;
                {project.xmlFileName || 'None uploaded'}
              </span>
              {xmlFile && (
                <span className="edit-modal__filename edit-modal__filename--new">
                  New: {xmlFile.name}
                </span>
              )}
            </div>
          </div>

          <div className="edit-modal__actions">
            <div className="edit-modal__primary-actions">
              <button
                type="button"
                className="edit-modal__btn edit-modal__btn--cancel"
                onClick={onClose}
                disabled={isBusy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="edit-modal__btn edit-modal__btn--submit"
                disabled={isBusy}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
            <button
              type="button"
              className="edit-modal__btn edit-modal__btn--danger"
              onClick={handleDelete}
              disabled={isBusy}
            >
              {deleting ? 'Deleting...' : 'Delete Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditProjectModal;
